import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import type { AdminPlaybookRow, PlaybookStatus } from "@/queries/adminPlaybooks";

interface Props {
  row: AdminPlaybookRow;
  onEdit: () => void;
}

const STATUS_VARIANT: Record<
  PlaybookStatus,
  "success" | "warning" | "muted" | "destructive"
> = {
  active: "success",
  paused: "warning",
  deprecated: "muted",
  deprecated_on_arrival: "destructive",
};

export function PlaybookRow({ row, onEdit }: Props) {
  const { t } = useTranslation("admin-playbooks");
  return (
    <TableRow>
      <TableCell className="font-medium">
        <Link
          to="/admin/playbooks/$id"
          params={{ id: row.id }}
          className="hover:underline"
        >
          {row.display_name}
        </Link>
        <div className="text-xs text-muted-foreground">{row.id}</div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {row.category}
      </TableCell>
      <TableCell className="text-right tabular-nums text-muted-foreground">
        v{row.schema_version}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[row.status]}>
          {t(`status.${row.status}`)}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={row.live_allowed ? "success" : "muted"}>
          {row.live_allowed ? "✓" : "—"}
        </Badge>
      </TableCell>
      <TableCell className="tabular-nums text-sm">
        {row.position_size_override ?? "—"}
      </TableCell>
      <TableCell className="text-right">
        <Button variant="outline" size="sm" onClick={onEdit}>
          {t("actions.edit")}
        </Button>
      </TableCell>
    </TableRow>
  );
}
